import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { tap } from 'rxjs/operators';
import { AppService } from '../../../app.service';
import { UtilsService } from '../../../shared/utils.service';
import { ContractorsAddComponent } from '../contractors-add/contractors-add.component';
import { ContractorModel } from 'src/app/models/contractor.model';
import { ContractorsDetailsComponent } from '../contractors-details/contractors-details.component';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { UserPublicProfileComponent } from '../../../shared/user-public-profile/user-public-profile.component';
import { saveAs } from 'file-saver';
import { RBACService } from '../../../shared/rbac.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-contractors',
  templateUrl: './contractors.component.html',
  styleUrls: ['./contractors.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ContractorsComponent implements OnInit, AfterViewInit {
  // RBAC permission flags: resolved once in ngOnInit().
  canCreate: boolean = false;
  canView: boolean = false;
  canEdit: boolean = false;
  canDelete: boolean = false;

  // Plan feature plus RBAC flag for the "Send Message" menu item, resolved once in
  // ngOnInit() to avoid re-evaluating the checks on every change detection cycle.
  canComposeMessages: boolean = false;

  // Build displayedColumns dynamically (NOT as a static array).
  displayedColumns: string[] = [];

  listDataSource = new ContractorsDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    private dialog: MatDialog,
    private api: ApiRequestService,
    public rbac: RBACService,
    private router: Router
  ) {}

  ngOnInit() {
    // 1. Resolve RBAC permission flags.
    this.canCreate = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_CREATE
        )
      ],
      true
    );
    this.canView = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_VIEW
        )
      ],
      true
    );
    this.canEdit = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_EDIT
        )
      ],
      true
    );
    this.canDelete = this.rbac.userCanAccess(
      [
        RBACService.getPermission(
          RBACService.RBAC_F.F_CONTRACTORS,
          RBACService.RBAC_A.A_DELETE
        )
      ],
      true
    );
    this.canComposeMessages =
      this.app.planCan(AppService.FEATURES.MESSAGES) &&
      this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      );

    // 2. Page-level access: redirect if user has NO permissions at all.
    if (!this.canCreate && !this.canView && !this.canEdit && !this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to access contractors.'
      );
      this.router.navigate(['/dashboard']);
      return;
    }

    // 3. Build displayedColumns dynamically.
    this.displayedColumns = [];
    if (this.canView || this.canDelete) {
      this.displayedColumns.push('select');
    }
    this.displayedColumns.push('icons', 'name', 'contact_person', 'industry');

    // Exclude the score from free accounts
    if (this.app.account.subscription != 'Free') {
      this.displayedColumns.push('pre_qualification_score');
    }
    // Add list actions
    this.displayedColumns.push('date_created');
    // this.displayedColumns.push('date_created_UTC');
    this.displayedColumns.push('actions');

    // 4. Only load data if user has view permission.
    if (this.canView) {
      this.listDataSource.getData();
    }
  }

  ngAfterViewInit() {
    // The table, its matSort and its mat-paginator all live inside a container gated by
    // *ngIf="canView", so without that permission Angular never creates those view
    // children and this.sort / this.paginator stay undefined. Bail out before wiring them up.
    if (!this.canView) {
      return;
    }

    // reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.listDataSource.limit = this.paginator.pageSize;
      this.listDataSource.offset = this.paginator.pageIndex;
      this.listDataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.listDataSource.order_by = 'date_created';
      } else {
        this.listDataSource.order_by = this.sort.active;
      }

      this.listDataSource.getData();
    });

    // subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Adding a contractor.
   */
  onAddContractor() {
    if (!this.canCreate) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to create contractors.'
      );
      return;
    }
    this.utils.showComponentDialog(
      ContractorsAddComponent,
      {},
      {
        width: '900px'
      },
      () => {
        if (this.canView) {
          this.listDataSource.getData();
        }
      }
    );
  }

  onExport(type: string = 'csv', id?: number) {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to export contractors.'
      );
      return;
    }
    let ids: number[] = [];
    if (id) {
      ids.push(id);
    } else {
      ids.push(...this.listDataSource.selection.selected);
    }

    // Get the current date object
    const date = new Date();

    this.api
      .makeDownloadRequest(
        `v1/contractors/export/${type}` +
          (ids.length > 0 ? '/' + ids.join(',') : ''),
        {},
        {
          order_by: this.listDataSource.order_by,
          order: this.listDataSource.order
        }
      )
      .then((response) => {
        saveAs(
          response,
          `${this.utils.getLangTerm('contractors.plural', 'Contractors')} - ${
            this.app.account.name
          } - ${date.getFullYear()}${date.getMonth() +
            1}${date.getDate()}.${type}`
        );
      });
  }

  onView(contractor: ContractorModel) {
    this.dialog
      .open(ContractorsDetailsComponent, {
        width: '500px',
        data: contractor
      })
      .afterClosed()
      .subscribe((success) => {
        if (!success) {
          return;
        }
        this.listDataSource.getData(true);
      });
  }

  /**
   *
   * @param contractor_account_id
   *
   * remove a contractor from the actions list.
   */
  onRemove(contractorAccountId: number) {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete contractors.'
      );
      return;
    }
    this.utils.showModal(
      'Remove ' + this.utils.getLangTerm('contractors.singular', 'Contractor'),
      'Are you sure you want to remove this ' +
        this.utils.getLangTerm('contractors.singular', 'Contractor') +
        '?',
      () => {
        this.api
          .makeRequest('delete', `v2/contractors/${contractorAccountId}`)
          .then(() => {
            this.listDataSource.getData();
            this.utils.showToast(
              'The ' +
                this.utils.getLangTerm('contractors.singular', 'Contractor') +
                ' was removed.'
            );
          })
          .catch((error) => {
            this.utils.handleAPIErrors(error);
          });
      }
    );
  }

  /**
   * remove a contractor from the list of selected checkboxes.
   */
  onRemoveSelected() {
    if (!this.canDelete) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to delete contractors.'
      );
      return;
    }
    this.utils.showModal(
      'Remove Selected ' +
        this.utils.getLangTerm('contractors.plural', 'Contractors'),
      'Are you sure you want to remove the selected ' +
        this.utils.getLangTerm('contractors.plural', 'Contractors') +
        '?',
      () => {
        const contractorAccountIds = this.listDataSource.selection.selected.join(
          ','
        );
        this.api
          .makeRequest('delete', `v2/contractors/${contractorAccountIds}`)
          .then(() => {
            this.listDataSource.selection.clear();
            this.listDataSource.getData();
            this.utils.showToast(
              'The selected ' +
                this.utils.getLangTerm('contractors.plural', 'Contractors') +
                ' was removed.'
            );
          })
          .catch((error) => {
            this.utils.handleAPIErrors(error);
          });
      }
    );
  }

  composeNewMessage() {
    if (!this.canView) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to view contractors.'
      );
      return;
    }
    if (
      !this.rbac.userCanAccess(
        [
          RBACService.getPermission(
            RBACService.RBAC_F.F_MESSAGES,
            RBACService.RBAC_A.A_CREATE
          )
        ],
        true
      )
    ) {
      this.rbac.showGenericAccessDeniedMessage(
        'You do not have permission to send messages.'
      );
      return;
    }
    if (this.listDataSource.selection.selected.length == 0) {
      this.utils.showToast(
        'You need to select some ' +
          this.utils.getLangTerm('contractors.plural', 'Contractors') +
          ' first.'
      );
      return; // early termination
    }

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectAccountIds: this.listDataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  onUserPublicView(hash: string) {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.listDataSource.getData();
      }
    );
  }

  protected readonly RBACService = RBACService;
  protected readonly AppService = AppService;
}

export class ContractorsDataSource extends ApiDataSource {
  order_by = 'name';
  order = 'asc';

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/contractors', resetOffset);
  }
}
